import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import RouteUploader from '../components/coordinator/RouteUploader';
import TechnicianManager from '../components/coordinator/TechnicianManager';
import RouteAssignment from '../components/coordinator/RouteAssignment';
import Toast from '../components/common/Toast';

const CoordinatorPage = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('dashboard');
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
    };

    const getTechnicianStats = () => {
        const tecnicos = user?.tecnicos || [];
        return {
            total: tecnicos.length,
            activos: tecnicos.filter(t => t.status === 'active').length,
            inactivos: tecnicos.filter(t => t.status === 'inactive').length,
            conRuta: tecnicos.filter(t => t.rutaAsignada).length
        };
    };

    const getRouteStats = () => {
        const rutas = user?.rutasDisponibles || [];
        return {
            total: rutas.length,
            asignadas: rutas.filter(r => r.asignada).length,
            disponibles: rutas.filter(r => !r.asignada).length
        };
    };

    const stats = getTechnicianStats();
    const routeStats = getRouteStats();

    return (
        <div className="coordinator-page">
            {/* Header */}
            <header className="coordinator-header">
                <div className="header-content">
                    <div className="welcome-section">
                        <h1>👨‍💼 Panel de Coordinación</h1>
                        <p>Bienvenido, {user?.name}</p>
                        <span className="region-badge">📍 {user?.region}</span>
                    </div>
                    <div className="header-actions">
                        <button
                            className="btn btn-danger"
                            onClick={handleLogout}
                        >
                            🚪 Salir
                        </button>
                    </div>
                </div>
            </header>

            {/* Navigation Tabs */}
            <nav className="coordinator-nav">
                <div className="nav-content">
                    <button
                        className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
                        onClick={() => setActiveTab('dashboard')}
                    >
                        📊 Dashboard
                    </button>
                    <button
                        className={`nav-tab ${activeTab === 'technicians' ? 'active' : ''}`}
                        onClick={() => setActiveTab('technicians')}
                    >
                        👥 Técnicos
                    </button>
                    <button
                        className={`nav-tab ${activeTab === 'routes' ? 'active' : ''}`}
                        onClick={() => setActiveTab('routes')}
                    >
                        🗺️ Gestión de Rutas
                    </button>
                    <button
                        className={`nav-tab ${activeTab === 'assignments' ? 'active' : ''}`}
                        onClick={() => setActiveTab('assignments')}
                    >
                        📋 Asignaciones
                    </button>
                </div>
            </nav>

            {/* Main Content */}
            <main className="coordinator-content">

                {/* Dashboard Tab */}
                {activeTab === 'dashboard' && (
                    <div className="dashboard-section">
                        <h2>📊 Resumen General</h2>

                        <div className="stats-grid">
                            <div className="stat-card technicians">
                                <div className="stat-header">
                                    <h3>👥 Técnicos</h3>
                                    <span className="stat-icon">👷‍♂️</span>
                                </div>
                                <div className="stat-body">
                                    <div className="stat-main">
                                        <span className="stat-number">{stats.total}</span>
                                        <span className="stat-label">Total</span>
                                    </div>
                                    <div className="stat-details">
                                        <div className="stat-item">
                                            <span className="stat-value">{stats.activos}</span>
                                            <span className="stat-desc">Activos</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-value">{stats.conRuta}</span>
                                            <span className="stat-desc">Con Ruta</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="stat-card routes">
                                <div className="stat-header">
                                    <h3>🗺️ Rutas</h3>
                                    <span className="stat-icon">📍</span>
                                </div>
                                <div className="stat-body">
                                    <div className="stat-main">
                                        <span className="stat-number">{routeStats.total}</span>
                                        <span className="stat-label">Total</span>
                                    </div>
                                    <div className="stat-details">
                                        <div className="stat-item">
                                            <span className="stat-value">{routeStats.asignadas}</span>
                                            <span className="stat-desc">Asignadas</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-value">{routeStats.disponibles}</span>
                                            <span className="stat-desc">Disponibles</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="stat-card productivity">
                                <div className="stat-header">
                                    <h3>📈 Productividad</h3>
                                    <span className="stat-icon">⚡</span>
                                </div>
                                <div className="stat-body">
                                    <div className="stat-main">
                                        <span className="stat-number">{Math.round((stats.conRuta / stats.total) * 100)}%</span>
                                        <span className="stat-label">Utilización</span>
                                    </div>
                                    <div className="stat-details">
                                        <div className="stat-item">
                                            <span className="stat-value">{stats.activos}</span>
                                            <span className="stat-desc">Trabajando</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-value">{stats.inactivos}</span>
                                            <span className="stat-desc">Libres</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Quick Actions */}
                        <div className="quick-actions">
                            <h3>⚡ Acciones Rápidas</h3>
                            <div className="action-buttons">
                                <button
                                    className="action-btn"
                                    onClick={() => setActiveTab('routes')}
                                >
                                    📁 Subir Nuevo KMZ
                                </button>
                                <button
                                    className="action-btn"
                                    onClick={() => setActiveTab('assignments')}
                                >
                                    📋 Asignar Rutas
                                </button>
                                <button
                                    className="action-btn"
                                    onClick={() => setActiveTab('technicians')}
                                >
                                    👥 Ver Técnicos
                                </button>
                            </div>
                        </div>

                        {/* Recent Activity */}
                        <div className="recent-activity">
                            <h3>📝 Actividad Reciente</h3>
                            <div className="activity-list">
                                <div className="activity-item">
                                    <span className="activity-icon">✅</span>
                                    <div className="activity-content">
                                        <p><strong>Juan Pérez</strong> completó ruta "Las Flores"</p>
                                        <small>Hace 2 horas</small>
                                    </div>
                                </div>
                                <div className="activity-item">
                                    <span className="activity-icon">📋</span>
                                    <div className="activity-content">
                                        <p>Asignaste ruta "Industrial Apodaca" a <strong>María González</strong></p>
                                        <small>Hace 4 horas</small>
                                    </div>
                                </div>
                                <div className="activity-item">
                                    <span className="activity-icon">📁</span>
                                    <div className="activity-content">
                                        <p>Se subió nuevo archivo KMZ "Zona Centro"</p>
                                        <small>Ayer</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Technicians Tab */}
                {activeTab === 'technicians' && (
                    <TechnicianManager
                        tecnicos={user?.tecnicos || []}
                        onToast={showToast}
                    />
                )}

                {/* Routes Tab */}
                {activeTab === 'routes' && (
                    <RouteUploader
                        rutas={user?.rutasDisponibles || []}
                        onToast={showToast}
                    />
                )}

                {/* Assignments Tab */}
                {activeTab === 'assignments' && (
                    <RouteAssignment
                        tecnicos={user?.tecnicos || []}
                        rutas={user?.rutasDisponibles || []}
                        onToast={showToast}
                    />
                )}

            </main>

            {/* Toast Notifications */}
            <Toast
                show={toast.show}
                message={toast.message}
                type={toast.type}
                onClose={() => setToast({ show: false, message: '', type: '' })}
            />

            <style jsx>{`
        .coordinator-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        }

        .coordinator-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 20px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .header-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .welcome-section h1 {
          margin: 0 0 5px 0;
          font-size: 1.8rem;
          font-weight: 600;
        }

        .welcome-section p {
          margin: 0 0 5px 0;
          opacity: 0.9;
        }

        .region-badge {
          background: rgba(255,255,255,0.2);
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.85rem;
        }

        .coordinator-nav {
          background: white;
          border-bottom: 1px solid #e9ecef;
          padding: 0 20px;
        }

        .nav-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          gap: 0;
        }

        .nav-tab {
          padding: 15px 20px;
          border: none;
          background: transparent;
          color: #666;
          font-weight: 500;
          cursor: pointer;
          border-bottom: 3px solid transparent;
          transition: all 0.3s ease;
        }

        .nav-tab:hover {
          color: #667eea;
          background: #f8f9fa;
        }

        .nav-tab.active {
          color: #667eea;
          border-bottom-color: #667eea;
          background: #f8f9ff;
        }

        .coordinator-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 30px 20px;
        }

        .dashboard-section h2 {
          color: #333;
          margin-bottom: 30px;
          font-size: 1.5rem;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }

        .stat-card {
          background: white;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.07);
          border-left: 4px solid #667eea;
        }

        .stat-card.technicians {
          border-left-color: #28a745;
        }

        .stat-card.routes {
          border-left-color: #007bff;
        }

        .stat-card.productivity {
          border-left-color: #ffc107;
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
        }

        .stat-header h3 {
          margin: 0;
          color: #333;
          font-size: 1.1rem;
        }

        .stat-icon {
          font-size: 1.5rem;
          opacity: 0.7;
        }

        .stat-body {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .stat-main {
          display: flex;
          flex-direction: column;
        }

        .stat-number {
          font-size: 2.5rem;
          font-weight: 700;
          color: #333;
          line-height: 1;
        }

        .stat-label {
          color: #666;
          font-size: 0.9rem;
          font-weight: 500;
        }

        .stat-details {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .stat-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .stat-value {
          font-size: 1.2rem;
          font-weight: 600;
          color: #667eea;
        }

        .stat-desc {
          font-size: 0.8rem;
          color: #666;
        }

        .quick-actions {
          background: white;
          border-radius: 12px;
          padding: 25px;
          margin-bottom: 30px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.07);
        }

        .quick-actions h3 {
          margin: 0 0 20px 0;
          color: #333;
        }

        .action-buttons {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 15px;
        }

        .action-btn {
          padding: 15px 20px;
          border: 2px dashed #667eea;
          background: #f8f9ff;
          color: #667eea;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .action-btn:hover {
          background: #667eea;
          color: white;
          border-style: solid;
        }

        .recent-activity {
          background: white;
          border-radius: 12px;
          padding: 25px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.07);
        }

        .recent-activity h3 {
          margin: 0 0 20px 0;
          color: #333;
        }

        .activity-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .activity-item {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .activity-icon {
          font-size: 1.2rem;
          width: 30px;
          text-align: center;
        }

        .activity-content p {
          margin: 0 0 2px 0;
          color: #333;
        }

        .activity-content small {
          color: #666;
          font-size: 0.85rem;
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 15px;
            text-align: center;
          }

          .nav-content {
            flex-wrap: wrap;
          }

          .nav-tab {
            flex: 1;
            min-width: 120px;
            font-size: 0.9rem;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .stat-body {
            flex-direction: column;
            gap: 15px;
            align-items: flex-start;
          }

          .stat-details {
            flex-direction: row;
            gap: 20px;
            width: 100%;
          }

          .action-buttons {
            grid-template-columns: 1fr;
          }

          .activity-item {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>
        </div>
    );
};

export default CoordinatorPage;